import React from "react";
import { Popover, Box, Button } from "@mui/material";
import { format, parseISO, isValid } from "date-fns";
import { DashboardCalendarPicker } from "./DashboardCalendarPicker";
import "react-datepicker/dist/react-datepicker.css";

interface CustomDateRangePopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  startDate: string;
  endDate: string;
  onClose: () => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onApply: () => void;
  onClear: () => void;
}

const toDate = (value: string): Date | null => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

const toIsoDate = (date: Date | null): string => {
  if (!date || !isValid(date)) return "";
  return format(date, "yyyy-MM-dd");
};

export const CustomDateRangePopover: React.FC<CustomDateRangePopoverProps> = ({
  open,
  anchorEl,
  startDate,
  endDate,
  onClose,
  onStartDateChange,
  onEndDateChange,
  onApply,
  onClear,
}) => {
  const selectedStart = toDate(startDate);
  const selectedEnd = toDate(endDate);
  const hasSelection = Boolean(startDate || endDate);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            p: 2,
            borderRadius: "10px",
            border: "1px solid #d4c8c9",
            boxShadow:
              "0 2px 4px rgba(42, 18, 20, 0.08), 0 10px 24px rgba(42, 18, 20, 0.12)",
            overflow: "visible",
          },
        },
      }}
    >
      <Box>
        <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
          <Box>
            <Box
              sx={{
                mb: 1,
                fontWeight: 600,
                fontSize: "12px",
                color: "#4b5563",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              From Date
            </Box>
            <DashboardCalendarPicker
              selected={selectedStart}
              onChange={(date) => {
                const nextStart = toIsoDate(date);
                onStartDateChange(nextStart);
                if (nextStart && endDate && nextStart > endDate) {
                  onEndDateChange("");
                }
              }}
            />
          </Box>
          <Box>
            <Box
              sx={{
                mb: 1,
                fontWeight: 600,
                fontSize: "12px",
                color: "#4b5563",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              To Date
            </Box>
            <DashboardCalendarPicker
              selected={selectedEnd}
              onChange={(date) => {
                const nextEnd = toIsoDate(date);
                if (startDate && nextEnd && nextEnd < startDate) {
                  onEndDateChange("");
                  return;
                }
                onEndDateChange(nextEnd);
              }}
              minDate={selectedStart || undefined}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            px: 0.5,
            pt: 0.5,
            borderTop: "1px solid #d4c8c9",
          }}
        >
          <Button
            size="small"
            onClick={onClear}
            disabled={!hasSelection}
            sx={{
              textTransform: "none",
              fontFamily: "'Poppins', sans-serif",
              fontSize: "12px",
              color: "#d2363a",
              "&.Mui-disabled": { color: "#d1d5db" },
            }}
          >
            Clear
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              onClick={onClose}
              sx={{
                textTransform: "none",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "12px",
                color: "#6b7280",
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={!startDate || !endDate}
              onClick={onApply}
              sx={{
                textTransform: "none",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "12px",
                backgroundColor: "#d2363a",
                "&:hover": { backgroundColor: "#b92d31" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Box>
    </Popover>
  );
};
